import { IconArrowRight, IconBooks, IconBriefcase, IconCalendarEvent, IconMapPin, IconPhone, IconSpeakerphone, IconUser } from '@tabler/icons-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { PosterLogo, PosterSheet, fs, hexToRgba, numberScale, posterFields, valueFitScale } from '../posterParts';

/**
 * "Sidebar" — an asymmetric layout: a full-height colour band on the left carries the logo, the two dates (as calendar tiles) and the
 * vacancy count; the right column reads top-down like a clean notice (title, post, icon rows, call-to-action, shop card).
 * Palette: band, bandDeep, bandText, accent, bg, ink, muted, line, cta, ctaText.
 */
const FONT = "'Anek Bangla', 'Noto Sans Bengali', sans-serif";

function DateTile({ p, label, parts, hot = false }) {
    return (
        <div className="flex flex-col items-center overflow-hidden text-center" style={{ background: '#fff', borderRadius: '1.8cqw', boxShadow: '0 0.6cqw 1.4cqw rgba(0,0,0,0.25)' }}>
            <div style={{ width: '100%', background: hot ? p.accent : hexToRgba(p.band, 0.9), color: hot ? p.ink : '#fff', fontSize: fs(2.3), fontWeight: 800, padding: `${fs(0.7)} 0`, lineHeight: 1.2 }}>{label}</div>
            <div style={{ color: p.ink, fontSize: fs(7.6), fontWeight: 900, lineHeight: 1, paddingTop: fs(1) }}>{parts.day}</div>
            <div style={{ color: p.muted, fontSize: fs(2.8), fontWeight: 700, lineHeight: 1.1, padding: `${fs(0.3)} 0 ${fs(1.2)}` }}>{parts.month}</div>
        </div>
    );
}

function Row({ p, Icon, label, children, last = false }) {
    return (
        <div className="flex items-start" style={{ gap: fs(2.6), padding: `${fs(2.2)} 0`, borderBottom: last ? 'none' : `0.25cqw solid ${p.line}` }}>
            <div className="flex flex-none items-center justify-center rounded-full" style={{ width: fs(8.4), height: fs(8.4), background: p.band }}>
                <Icon style={{ width: fs(4.6), height: fs(4.6), color: p.bandText }} stroke={2} />
            </div>
            <div className="min-w-0 flex-1">
                <div style={{ color: p.muted, fontSize: fs(2.6), fontWeight: 700, lineHeight: 1.2 }}>{label}</div>
                {children}
            </div>
        </div>
    );
}

export default function SidebarDesign({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    const p = theme.palette;
    const f = posterFields(job, business, t);
    const eduScale = valueFitScale(f.edu, 34);
    const ageScale = valueFitScale(f.age, 26);

    const body = (
        <div className="grid flex-1" style={{ gridTemplateColumns: '29cqw 1fr' }}>
            {/* ── the band ── */}
            <div
                className="relative flex flex-col items-center"
                style={{
                    padding: `${fs(5.5)} 2.6cqw ${fs(4)}`,
                    gap: fs(3.2),
                    color: p.bandText,
                    background: `repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0 1.6cqw, rgba(255,255,255,0) 1.6cqw 3.2cqw), linear-gradient(180deg, ${p.band} 0%, ${p.bandDeep} 100%)`,
                }}
            >
                <PosterLogo job={job} size={19} style={{ background: '#fff', borderRadius: '50%', border: `${fs(0.7)} solid ${p.accent}`, padding: fs(1.4), boxShadow: '0 0.8cqw 1.8cqw rgba(0,0,0,0.3)' }} />

                <div className="flex flex-col" style={{ gap: fs(2), width: '100%' }}>
                    <DateTile p={p} label={f.label.start} parts={f.startParts} />
                    <DateTile p={p} label={f.label.end} parts={f.endParts} hot />
                </div>

                <div className="flex flex-1 items-center justify-center" style={{ opacity: 0.16 }}>
                    <IconBriefcase style={{ width: fs(15), height: fs(15) }} stroke={1.3} />
                </div>

                <div className="flex flex-col items-center justify-end text-center">
                    <div style={{ width: fs(6), height: '0.5cqw', background: p.accent, marginBottom: fs(2), borderRadius: '99px' }} />
                    <div style={{ fontSize: fs(2.8), fontWeight: 800, lineHeight: 1.2, opacity: 0.95 }}>{f.label.vacancy}</div>
                    <div style={{ color: p.accent, fontSize: `calc(${13 * numberScale(f.vacancy)}cqw * var(--fs, 1))`, fontWeight: 900, lineHeight: 1, marginTop: fs(0.6) }}>{f.vacancy}</div>
                    <div style={{ fontSize: fs(3.6), fontWeight: 800, lineHeight: 1.2 }}>{f.label.unit}</div>
                </div>
            </div>

            {/* ── the notice ── */}
            <div className="flex min-w-0 flex-col" style={{ background: p.bg, padding: `${fs(5.5)} 5cqw ${fs(4)} 4.6cqw`, gap: fs(2.4) }}>
                <div className="flex items-center self-start" style={{ background: p.accent, color: p.ink, borderRadius: '99px', padding: `${fs(0.9)} ${fs(2.4)}`, gap: fs(1.2), fontSize: fs(2.8), fontWeight: 900 }}>
                    <IconSpeakerphone style={{ width: fs(3.4), height: fs(3.4) }} stroke={2.4} />
                    <div>{f.label.notice}</div>
                </div>

                <div>
                    <div style={{ color: p.ink, fontSize: fs(6.6), fontWeight: 900, lineHeight: 1.12 }}>{f.org}</div>
                    <div style={{ width: fs(15), height: '1cqw', background: p.band, borderRadius: '99px', marginTop: fs(1.8) }} />
                    {f.position && <div style={{ color: p.muted, fontSize: fs(3.6), fontWeight: 700, lineHeight: 1.3, marginTop: fs(1.8) }}>{f.position}</div>}
                </div>

                <div className="flex flex-col" style={{ marginTop: fs(0.6) }}>
                    <Row p={p} Icon={IconBooks} label={f.label.edu}>
                        <div style={{ color: p.ink, fontSize: `calc(${3.6 * eduScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.25 }}>{f.edu}</div>
                    </Row>
                    <Row p={p} Icon={IconUser} label={f.label.age}>
                        <div style={{ color: p.ink, fontSize: `calc(${3.6 * ageScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.25 }}>{f.age}</div>
                    </Row>
                    <Row p={p} Icon={IconCalendarEvent} label={f.label.end} last>
                        <div style={{ color: p.ink, fontSize: fs(3.6), fontWeight: 800 }}>{f.end}</div>
                    </Row>
                </div>

                <div style={{ color: p.muted, fontSize: fs(2.5), fontWeight: 600, lineHeight: 1.35 }}>✓ {f.label.promo}</div>

                <div className="flex items-center justify-between" style={{ background: p.cta, color: p.ctaText, borderRadius: '2cqw', padding: `${fs(2)} ${fs(2.6)}`, boxShadow: '0 0.7cqw 1.6cqw rgba(0,0,0,0.2)', gap: fs(1.6) }}>
                    <div style={{ fontSize: fs(3.8), fontWeight: 900, lineHeight: 1.2 }}>{f.label.cta}</div>
                    <IconArrowRight className="flex-none" style={{ width: fs(5.4), height: fs(5.4), color: p.accent }} stroke={3} />
                </div>

                {showFooter && (
                    <div className="mt-auto" style={{ paddingTop: fs(0.6) }}>
                        <div style={{ background: '#fff', border: `0.3cqw solid ${p.line}`, borderRadius: '2cqw', padding: `${fs(2.2)} ${fs(2.4)}`, boxShadow: '0 0.5cqw 1.2cqw rgba(15,23,42,0.08)' }}>
                            <div className="flex items-center" style={{ gap: fs(1.8) }}>
                                {f.shop.logo && <img src={f.shop.logo} alt="" className="flex-none rounded-full object-contain" style={{ width: fs(8), height: fs(8), border: `0.3cqw solid ${p.line}`, padding: '0.5cqw' }} />}
                                <div className="min-w-0" style={{ color: p.band, fontSize: fs(4.8), fontWeight: 900, lineHeight: 1.15, opacity: f.shop.placeholder ? 0.6 : 1 }}>{f.shop.nameShown}</div>
                            </div>
                            <div className="flex items-start" style={{ gap: fs(1), marginTop: fs(1.2), color: p.ink, fontSize: fs(3), lineHeight: 1.3, opacity: f.shop.addressPlaceholder ? 0.6 : 0.9 }}>
                                <IconMapPin className="flex-none" style={{ width: fs(3.4), height: fs(3.4), color: p.band, marginTop: '0.2cqw' }} />
                                <div>{f.shop.addressShown}</div>
                            </div>
                            {f.shop.phone && (
                                <div className="flex items-center" style={{ gap: fs(1), marginTop: fs(0.6), color: p.ink, fontSize: fs(3.8), fontWeight: 900 }}>
                                    <IconPhone className="flex-none" style={{ width: fs(3.6), height: fs(3.6), color: p.band }} />
                                    <div>{f.shop.phone}</div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <PosterSheet
            deps={[job, business, theme, showFooter, t]}
            className={className}
            fontFamily={FONT}
            background={p.bg}
            body={body}
        />
    );
}
